import React, { Dispatch, SetStateAction } from 'react';
import { ModalContent, Portal, RegularButton, Typography } from 'fronton-react';
import { Modal } from 'fronton-react/modal';
import { useBreakpoints } from 'common/hooks';
import { useTranslation } from 'react-i18next';
import { analyticsUtils } from 'utils';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import {
  backButtonStyle,
  imgStyle,
  imgWrap,
  modalContentStyle,
  modalContentWrapStyle,
  modalHeaderStyle,
} from './styles';
import { defineImgSize } from './utils/define-img-size';
import {
  DATA_QA_OPERATIONAL_TIME_MODAL_BACK_BUTTON,
  DATA_QA_OPERATIONAL_TIME_MODAL_PICTURE,
} from '../constants';
import { ModalContentType } from '../types';

export type OperationalTimeModalProps = {
  closeCb?: () => void;
  modalVisibility: boolean;
  setModalVisibility: Dispatch<SetStateAction<boolean>>;
  modalContent: ModalContentType;
  gaName: string;
};

export const OperationalTimeModal: React.FC<OperationalTimeModalProps> = ({
  closeCb,
  modalVisibility,
  setModalVisibility,
  modalContent,
  gaName,
}) => {
  const { imgSrc, modalName, title, content } = modalContent;
  const { smMobile, mobile } = useBreakpoints() || {
    smMobile: false,
    mobile: false,
  };

  const rest = defineImgSize(smMobile, mobile);
  const imgOptimized = useOptimisedImages({
    src: imgSrc,
    sizes: {
      rest,
    },
  });

  const { t } = useTranslation();

  const handleClose = () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage',
      elementName: `${gaName} / ${modalContent.title}`,
      elementText: 'Закрыть',
    });
    setModalVisibility(false);
    closeCb?.();
  };

  return (
    <div aria-label={`${modalName} модальное окно`}>
      <Portal>
        <Modal
          show={modalVisibility}
          modalID="operational-time"
          onClose={handleClose}
          size="s"
          variant={smMobile ? 'full-screen' : 'curtain'}
        >
          <div className={modalContentWrapStyle}>
            <div className={imgWrap}>
              <picture data-qa={DATA_QA_OPERATIONAL_TIME_MODAL_PICTURE}>
                <source srcSet={`${imgOptimized.rest?.xx} 2x, ${imgOptimized.rest?.x} 1x`} />
                <img
                  alt={`${modalName} картинка модального окна`}
                  className={imgStyle}
                  width={rest.w}
                  height={rest.h}
                  src={imgOptimized.rest?.x ?? undefined}
                  srcSet={`${imgOptimized.rest?.xx} 2x, ${imgOptimized.rest?.x} 1x`}
                />
              </picture>
            </div>
            <Typography
              data-qa="operational-time-modal-title"
              color="text-primary"
              as={smMobile ? 'h3' : 'h2'}
              className={modalHeaderStyle}
              variant={smMobile ? 'h3' : 'h2'}
            >
              {title}
            </Typography>
            <ModalContent data-qa="operational-time-modal-content" className={modalContentStyle}>
              {content}
            </ModalContent>
          </div>
          <RegularButton
            className={backButtonStyle}
            data-qa={DATA_QA_OPERATIONAL_TIME_MODAL_BACK_BUTTON}
            size="l"
            variant="secondary"
            onClick={handleClose}
          >
            {t('back')}
          </RegularButton>
        </Modal>
      </Portal>
    </div>
  );
};
